import React, { useState, useEffect, useCallback } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import { FFTableProps, Order, FFTableFilterValue } from './types';
import { useFFTableStyles } from './styles';
import FFTableHead from './FFTableHead';
import FFTableRow from './FFTableRow';
import FFTableExpandableRow from './FFTableExpandableRow';
import FFTablePagination from './FFTablePagination';
import FFTableFilters from './FFTableFilters';
import FFTableDateRangeFilter from './FFTableDateRangeFilter';
import { useLanguageContext } from '../../contexts/langContext';

// Helper functions for sorting
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Helper function to filter data
function applyFilters<T>(
  data: T[], 
  filters: FFTableFilterValue[], 
  fromDate: Date | null = null, 
  untilDate: Date | null = null
): T[] {
  // Start with an initial filtered set based on regular filters
  let filteredData = filters.length === 0 ? data : data.filter((item) => {
    return filters.every((filter) => {
      const value = (item as any)[filter.columnId];

      if (value === undefined || value === null) {
        return false;
      }

      switch (filter.operator) {
        case 'equals':
          if (typeof value === 'boolean') {
            return value === (filter.value === 'true');
          }
          return String(value) === String(filter.value);
        case 'contains':
          return String(value)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase());
        case 'startsWith':
          return String(value)
            .toLowerCase()
            .startsWith(String(filter.value).toLowerCase());
        case 'endsWith':
          return String(value)
            .toLowerCase()
            .endsWith(String(filter.value).toLowerCase());
        case 'greaterThan':
          return value > filter.value;
        case 'lessThan':
          return value < filter.value;
        default:
          return String(value)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase());
      }
    });
  });

  // Apply date range filtering if dates are provided
  if (fromDate || untilDate) {
    filteredData = filteredData.filter((item) => {
      // Attempt to find a date field in the item
      // This is a simplification - in a real implementation, you would specify which field to use
      const dateFields = Object.entries(item as any)
        .filter(([_, value]) => value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value as string))))
        .map(([key, _]) => key);

      if (dateFields.length === 0) return true; // No date fields to filter on

      return dateFields.some(field => {
        const itemDate = (item as any)[field] instanceof Date 
          ? (item as any)[field] 
          : new Date((item as any)[field]);
        
        if (fromDate && untilDate) {
          return itemDate >= fromDate && itemDate <= untilDate;
        } else if (fromDate) {
          return itemDate >= fromDate;
        } else if (untilDate) {
          return itemDate <= untilDate;
        }
        return true;
      });
    });
  }

  return filteredData;
}

export function FFTable<T extends object>({
  columns,
  rows,
  onActionClick,
  pagination,
  serverSide = false,
  loading = false,
  error = null,
  onFilterChange,
  initialFilters = [],
  filterColumnsOptions = [],
  onSortChange,
  initialSort = { orderBy: 'id', order: 'asc' },
  rowKey = 'id',
  emptyStateMessage = 'No data available',
  errorStateMessage = 'Error loading data',
  loadingStateMessage = 'Loading data...',
  className,
  style,
  customFilters,
  mandatoryDateRange,
  expandableRows,
  onRowClick,
}: FFTableProps<T>) {
  const classes = useFFTableStyles();
  const { dir } = useLanguageContext();

  const [order, setOrder] = useState<Order>(initialSort.order);
  const [orderBy, setOrderBy] = useState<string>(initialSort.orderBy);
  const [filters, setFilters] = useState<FFTableFilterValue[]>(initialFilters);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Apply client-side sorting and filtering if not server-side
  const displayData = !serverSide
    ? stableSort(
        applyFilters(
          rows, 
          filters, 
          mandatoryDateRange?.fromDate || null, 
          mandatoryDateRange?.untilDate || null
        ),
        getComparator(order, orderBy as any)
      )
    : rows;

  // Handle row expansion toggle
  const handleToggleExpand = (rowId: string | number) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // Handle sort request
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    if (serverSide && onSortChange) {
      onSortChange(property, newOrder);
    }
  };

  // Handle filter change
  const handleFilterChange = useCallback(
    (newFilters: FFTableFilterValue[]) => {
      setFilters(newFilters);

      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    },
    [onFilterChange]
  );

  // Render loading state
  const renderLoading = () => (
    <TableRow>
      <TableCell colSpan={expandableRows ? columns.length + 1 : columns.length} align="center" padding="normal">
        <Box p={3} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={40} />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            {loadingStateMessage}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  // Render error state
  const renderError = () => (
    <TableRow>
      <TableCell colSpan={expandableRows ? columns.length + 1 : columns.length} align="center" padding="normal">
        <Box p={3} className={classes.errorState}>
          <Typography variant="h6" color="error">
            {errorStateMessage}
          </Typography>
          {error && (
            <Typography variant="body2" color="error">
              {error.message}
            </Typography>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );

  // Render empty state
  const renderEmpty = () => (
    <TableRow>
      <TableCell colSpan={expandableRows ? columns.length + 1 : columns.length} align="center" padding="normal">
        <Box p={3} className={classes.emptyState}>
          <Typography variant="body1">{emptyStateMessage}</Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <div className={`${classes.root} ${className || ''}`} style={style}>
      {/* Filters Row */}
      <Box className={classes.filtersRow}>
        {/* Simple Filter */}
        {onFilterChange && (
          <FFTableFilters
            columns={columns.filter(
              (col) =>
                filterColumnsOptions.length === 0 ||
                filterColumnsOptions.includes(col.id.toString())
            )}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        )}
        
        {/* Mandatory Date Range Filter */}
        {mandatoryDateRange && (
          <FFTableDateRangeFilter
            fromDate={mandatoryDateRange.fromDate}
            untilDate={mandatoryDateRange.untilDate}
            onDateRangeChange={mandatoryDateRange.onDateRangeChange}
          />
        )}
        
        {customFilters && (
            <Box ml={1} display="flex" alignItems="center">
                {customFilters}
            </Box>
        )}
      </Box>

      {/* Table */}
      <Paper className={classes.paper}>
        <TableContainer className={classes.tableContainer}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <FFTableHead
              columns={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              serverSide={serverSide}
              hasExpandableRows={!!expandableRows}
            />
            <TableBody>
              {loading
                ? renderLoading()
                : error
                ? renderError()
                : displayData.length === 0
                ? renderEmpty()
                : displayData.map((row) => {
                    const rowId = (row as any)[rowKey];
                    
                    // Check if this row is expandable
                    const isExpandable = expandableRows && expandableRows.isRowExpandable(row);
                    
                    // Always use FFTableExpandableRow when expandableRows is provided,
                    // but set isExpandable based on the row's content
                    return expandableRows ? (
                      <FFTableExpandableRow
                        key={`row-${rowId}`}
                        row={row}
                        columns={columns}
                        onActionClick={onActionClick}
                        rowKey={rowKey}
                        isExpandable={isExpandable}
                        expanded={!!expandedRows[rowId]}
                        onToggleExpand={() => handleToggleExpand(rowId)}
                        renderExpandedContent={() => expandableRows.renderExpandedContent(row)}
                      />
                    ) : (
                      <FFTableRow
                        key={`row-${rowId}`}
                        row={row}
                        columns={columns}
                        onActionClick={onActionClick}
                        rowKey={rowKey}
                        onRowClick={() => onRowClick && onRowClick(row)}
                      />
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination && (
          <FFTablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onPageSizeChange}
            pageSizeOptions={pagination.pageSizeOptions}
          />
        )}
      </Paper>
    </div>
  );
}

export default FFTable;
